import {
  pageContextDescriptorSchema,
  type PageContextDescriptor,
} from "@repo/app-types/agents/pageContext";
import { useMemo } from "react";
import { useMatches, type UIMatch } from "react-router";
import z from "zod";

/**
 * Builds one fragment of the raw page-context descriptor for a matched route.
 * Fragments are merged from the shallowest match to the deepest, so a parent
 * playbook route can contribute `playbookId` while a nested teammate route
 * overrides `page` and contributes `teammateId`. A slideover can likewise
 * override `page` for the resource in front without restating ancestor scope.
 * The merged descriptor is validated once after every fragment is applied.
 */
export type BuildPageContext = (match: UIMatch) => Record<string, unknown>;

const pageContextHandle = z.object({
  pageContext: z.custom<BuildPageContext>(
    (value) => typeof value === "function",
  ),
});

function checkIsMatchWithPageContext<T extends UIMatch>(
  match: T,
): match is T & { handle: z.infer<typeof pageContextHandle> } {
  return pageContextHandle.safeParse(match.handle).success;
}

/**
 * Derives the page-context descriptor from all matched route fragments, or
 * undefined when none carries page context. Ids are validated (and branded) by
 * the schema, so a malformed param yields undefined rather than a broken
 * descriptor.
 */
export function buildPageContextDescriptor(
  matches: readonly UIMatch[],
): PageContextDescriptor | undefined {
  const matchesWithPageContext = matches.filter(checkIsMatchWithPageContext);
  if (matchesWithPageContext.length === 0) {
    return undefined;
  }
  const rawDescriptor = Object.assign(
    {},
    ...matchesWithPageContext.map((match) => match.handle.pageContext(match)),
  );
  const parsed = pageContextDescriptorSchema.safeParse(rawDescriptor);
  return parsed.success ? parsed.data : undefined;
}

/** The descriptor for the current route, recomputed as the user navigates. */
export function useResearchAgentPageContext():
  | PageContextDescriptor
  | undefined {
  const matches = useMatches();
  return useMemo(() => buildPageContextDescriptor(matches), [matches]);
}
